import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiCornerUpLeft, FiEdit2, FiTrash2, FiShare, FiMapPin, FiMoreHorizontal, FiCopy,
} from "react-icons/fi";
import {
  setReplyingTo,
  setEditingMessage,
  setForwardingMessage,
  toggleReactionThunk,
  deleteMessageThunk,
  pinMessageThunk,
  unpinMessageThunk,
} from "../../../Slices/chatSlice";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🔥"];

const MessageActions = ({ message, isOwn, conversationId }) => {
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const [showMore, setShowMore] = useState(false);
  // showReactions removed — all 6 quick emojis now render inline in the action bar

  const userName = loginUser?.firstname
    ? `${loginUser.firstname} ${loginUser.lastname || ""}`.trim()
    : "User";

  const handleReaction = (emoji) => {
    dispatch(toggleReactionThunk({
      messageId: message.id,
      conversationId,
      emoji,
      userId: loginUser?.id,
      userName,
    }));
  };

  const handleReply = () => {
    dispatch(setReplyingTo({
      messageId: message.id,
      senderName: message.senderName,
      content: (message.content || "").substring(0, 100),
      type: message.type,
    }));
    setShowMore(false);
  };

  const handleEdit = () => {
    dispatch(setEditingMessage({
      messageId: message.id,
      content: message.content,
    }));
    setShowMore(false);
  };

  const handleDelete = () => {
    if (window.confirm("Delete this message?")) {
      dispatch(deleteMessageThunk({
        messageId: message.id,
        conversationId,
        userId: loginUser?.id,
      }));
    }
    setShowMore(false);
  };

  const handlePin = () => {
    if (message.pinnedBy) {
      dispatch(unpinMessageThunk({ messageId: message.id, conversationId }));
    } else {
      dispatch(pinMessageThunk({
        messageId: message.id,
        conversationId,
        userId: loginUser?.id,
      }));
    }
    setShowMore(false);
  };

  const handleForward = () => {
    dispatch(setForwardingMessage({
      messageId: message.id,
      content: message.content,
      type: message.type,
    }));
    setShowMore(false);
  };

  // Copy message content (or filename for media) to clipboard. Matches the mobile Copy behavior.
  const handleCopy = async () => {
    const textToCopy = message.content || message.fileName || message.fileUrl || "";
    if (!textToCopy) return;
    try { await navigator.clipboard.writeText(textToCopy); } catch { /* silent — most browsers allow this on user gesture */ }
    setShowMore(false);
  };

  if (message.isDeleted || message.type === "system") return null;

  return (
    // Position to the SIDE of the bubble (iMessage / WhatsApp Web pattern) — never overlaps
    // the message text. Own messages (right-aligned): toolbar appears on the LEFT of the bubble.
    // Other people's messages (left-aligned): toolbar appears on the RIGHT.
    // The horizontal padding acts as an invisible hover bridge so the mouse can cross from the
    // bubble into the buttons without briefly leaving the row's hover area.
    <div
      className="msg-actions"
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        ...(isOwn
          ? { right: "100%", paddingRight: 6 }
          : { left: "100%", paddingLeft: 6 }),
        zIndex: 10,
      }}
    >
      {/* Action bar — all 6 quick reactions inline, then reply + more.
          No more separate "react" toggle: emojis are one-click. */}
      <div className="msg-actions__bar">
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            className="msg-actions__emoji-btn"
            onClick={() => handleReaction(emoji)}
            title={`React ${emoji}`}
            type="button"
          >
            {emoji}
          </button>
        ))}
        <div style={{ width: 1, alignSelf: "stretch", background: "#e2e8f0", margin: "0 2px" }} />
        <button className="msg-actions__btn" onClick={handleReply} title="Reply" type="button">
          <FiCornerUpLeft size={14} />
        </button>
        <button
          className="msg-actions__btn"
          onClick={() => setShowMore(!showMore)}
          title="More"
          type="button"
        >
          <FiMoreHorizontal size={14} />
        </button>
      </div>

      {/* More dropdown */}
      {showMore && (
        <div className="msg-actions__dropdown">
          {isOwn && (
            <button className="msg-actions__dropdown-item" onClick={handleEdit} type="button">
              <FiEdit2 size={14} /> Edit
            </button>
          )}
          {isOwn && (
            <button className="msg-actions__dropdown-item msg-actions__dropdown-item--danger" onClick={handleDelete} type="button">
              <FiTrash2 size={14} /> Delete
            </button>
          )}
          <button className="msg-actions__dropdown-item" onClick={handleCopy} type="button">
            <FiCopy size={14} /> Copy
          </button>
          <button className="msg-actions__dropdown-item" onClick={handlePin} type="button">
            <FiMapPin size={14} /> {message.pinnedBy ? "Unpin" : "Pin"}
          </button>
          <button className="msg-actions__dropdown-item" onClick={handleForward} type="button">
            <FiShare size={14} /> Forward
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageActions;
